import React from 'react';
import ApolloProviderWithAuth from './services/ApolloProviderWithAuth'; // Adjust path as needed
import { AppDataProvider } from './context/AppDataContext.jsx';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.scss';
import Sidebar from './Sidebar.jsx';
import BrandBar from './components/shared/BrandBar.jsx';
import LoginPage from './components/shared/LoginPage.jsx';
import AppFooter from './components/shared/AppFooter.jsx';
import PoliciesPage from './pages/PoliciesPage.jsx';
import InviteAccept from './pages/InviteAccept/InviteAccept.jsx';

import { useMsal } from '@azure/msal-react';
import { loginRequest } from './authConfig';
import { useMeQuery } from './services/authContext';
import { trackEvent } from './services/appInsights';
import { usePageTracking } from './hooks/usePageTracking';
import Category from './pages/util/Category.jsx';
import OutlookWrapper from './pages/OutlookWrapper.jsx';
import NotFoundPage from './pages/NotFoundPage.jsx';

// Util pages
import Profile from './pages/util/Profile.jsx';
import AdminScreen from './pages/util/admin/AdminScreen.jsx';

import UserSnap from './components/shared/UserSnap.jsx';
import RecentPublications from './pages/RecentPublications';

function AppContent({ isLoggedIn, handleLogin, handleLogout, authEnabled }) {
  usePageTracking();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const { isAdmin: isAdminFromMe, isDeveloper: isDeveloperFromMe } = useMeQuery();
  const isAdmin = authEnabled ? isAdminFromMe : true;
  const isDeveloper = authEnabled ? isDeveloperFromMe : true;
  const location = useLocation();

  React.useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  return (
    <div className="dashboard-layout">
      <UserSnap />
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}
      <div className={`sidebar-container${sidebarOpen ? ' sidebar-container--open' : ''}`}>
        <Sidebar isAdmin={isAdmin} isDeveloper={isDeveloper} isLoggedIn={isLoggedIn} />
      </div>
      <div className="main-content">
        <BrandBar
          isLoggedIn={isLoggedIn}
          handleLogin={handleLogin}
          handleLogout={handleLogout}
          authEnabled={authEnabled}
          onMenuToggle={() => setSidebarOpen(o => !o)}
          sidebarOpen={sidebarOpen}
        />
        <div className="main-area">
          <Routes>
            <Route path="/" element={<Category />} />

            <Route path="/recent-news" element={<Navigate to="/recent-publications" replace />} />
            <Route path="/recent-publications" element={<RecentPublications />} />

            {/* Dynamic global and regional routes */}
            <Route path="/g/:outlookId/:view/*" element={<OutlookWrapper />} />
            <Route path="/r/:outlookId/:view/*" element={<OutlookWrapper />} />

            <Route path="/u/admin" element={<AdminScreen />} />
            <Route path="/u/profile" element={<Profile />} />
            <Route path="/privacy" element={<PoliciesPage />} />

            {/* Catch-all route for 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
        <AppFooter />
      </div>
    </div>
  );
}

function App() {
  const { instance, accounts } = useMsal();
  // Configurable auth toggle via Vite env: VITE_AUTH_ENABLED
  // Set VITE_AUTH_ENABLED=false in local env to disable auth for development/testing
  const authEnabled = (() => {
    try {
      // import.meta.env is available under Vite; fall back to false when missing
      const raw = import.meta.env?.VITE_AUTH_ENABLED;
      if (raw === undefined || raw === null) return false;
      return String(raw).toLowerCase() === 'true';
    } catch (e) {
      return false;
    }
  })();

  // Determine login state depending on whether auth is enabled
  const isLoggedIn = authEnabled ? accounts.length > 0 : true;

  React.useEffect(() => {
    if (isLoggedIn && authEnabled) {
      trackEvent('Login', { method: 'MSAL_redirect' });
    }
  }, [isLoggedIn]);

  // When auth is disabled, make login/logout no-ops to avoid unexpected redirects
  const handleLogin = () => {
    if (!authEnabled) {
      console.log('Auth disabled (VITE_AUTH_ENABLED=false) — skipping login.');
      return;
    }
    instance.loginRedirect(loginRequest);
  };
  const handleLogout = () => {
    if (!authEnabled) {
      console.log('Auth disabled (VITE_AUTH_ENABLED=false) — skipping logout.');
      return;
    }
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  // Always render policies page standalone, regardless of auth state
  if (window.location.pathname.startsWith('/privacy')) {
    return <PoliciesPage />;
  }

  // Render invite acceptance page without auth — user has no account yet
  if (window.location.pathname.startsWith('/invite/')) {
    return <InviteAccept />;
  }

  if (!isLoggedIn) {
    return <LoginPage />;
  }

  return (
    <ApolloProviderWithAuth>
      <AppDataProvider>
        <Router>
          <AppContent
            isLoggedIn={isLoggedIn}
            handleLogin={handleLogin}
            handleLogout={handleLogout}
            authEnabled={authEnabled}
          />
        </Router>
      </AppDataProvider>
    </ApolloProviderWithAuth>
  );
}

export default App;
